import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { DropdownPosition, NgSelectComponent, NgSelectModule } from "@ng-select/ng-select";
import { TextButtonComponent } from "../../../../shared/buttons/text-button/text-button.component";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgClass } from "@angular/common";
import { BehaviorSubject, Observable, Subject, catchError, concat, debounceTime, distinctUntilChanged, firstValueFrom, fromEvent, map, merge, of, startWith, switchMap, takeUntil, tap, throttleTime } from "rxjs";
import { NTSTranslatePipe } from "../../../../pipe/nts-translation-pipe";
import { NgOptionHighlightModule } from "@ng-select/ng-option-highlight";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PopperHelper } from "@nts/std/utility";
import { MessageResourceManager } from "../../../../../resources/message-resource-manager";
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ObserveVisibilityDirective } from "../../observe-visibility/observe-visibility.directive";
import { BigNumber } from "@nts/std/types";
import type { UICommandInterface } from '@nts/std/interfaces';
import { ViewReadyCoordinator } from "../../view-ready-coordinator";

let nextId = 0;

export interface BaseExternalBoxComponentInterface {
    clearPoppers(): void;
    toggleDropdown(): void;
    hide(): void;
    show(): void;
}

export class ExternalBoxCommandUtility {

    constructor(
        private instance: BaseExternalBoxComponentInterface
    ) { }

    clearPoppers() {
        this.instance.clearPoppers();
    }

    closeDropdown() {
        this.instance.hide();
    }

    openDropdown() {
        this.instance.show();
    }

    toggleDropdown() {
        this.instance.toggleDropdown();
    }
}

@UntilDestroy()
@Component({
    selector: 'nts-base-external-box',
    templateUrl: './base-external-box.component.html',
    styleUrls: ['./base-external-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    NgSelectModule,
    TextButtonComponent,
    NgxPopperjsModule,
    FormsModule,
    NTSTranslatePipe,
    AsyncPipe,
    NgOptionHighlightModule,
    NgClass,
    ObserveVisibilityDirective
]
})
export class BaseExternalBoxComponent implements OnInit, OnChanges, BaseExternalBoxComponentInterface, AfterViewInit {

    /**
     * se viene passato il css selector disabilita il comportamento di calcolo automatico del parent scrollabile
     */
    @Input() appendTo = null;

    @Input() errorList: string[] = [];

    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() addItemCommand: UICommandInterface = null;
    @Input() copyCommand: UICommandInterface = null;
    @Input() viewItemCommand: UICommandInterface = null;
    @Input() searchCommand: UICommandInterface = null;
    @Input() dropdownPosition: DropdownPosition = 'auto';
    @Input() openDropdownOnUpDownArrowKey: boolean = true;

    @Input() isDropdownOpen = new BehaviorSubject<boolean>(false);
    @Input() showCodeInDescription = false;
    @Input() showDebugInfo = false;
    @Input() showDecodeError = false;
    @Input() isSearchable = true;
    /**
     * Lunghezza minima per iniziare la ricerca
     */
    @Input() minSearchLength: number = 3;

    @Input() typeToSearchText = MessageResourceManager.Current.getMessageWithStrings('std_AutoCompleteMinLengthMessage', this.minSearchLength.toString());

    /**
     * Se si passa una base fixed identity viene utilizzata come base per la identity da utilizzare per le chiamate
     */
    @Input() basePlainPascalCaseFixedIdentity: { [key: string]: string | BigNumber | number; } | null = null;

    /**
     * Disabilita il componente
     */
    @Input() isDisabled = false;

    @Input() isLocked: boolean = false;
    @Input() isLockedMessage = MessageResourceManager.Current.getMessage('std_FieldLockedMessage');
    @Input() placeholder: string;
    private _defaultPlaceholder: string = MessageResourceManager.Current.getMessage('std_NotDefined');

    @Input() defaultBorderColor = null;
    @Input() activeBorderColor = null;
    @Input() hoverBorderColor = null;

    /**
     * Valore iniziale dell'autocomplete
     * Sevalue.identity è valorizzato ma value.description non viene passato o è nullo viene usata la callback getItemFromIdentityCallback per recuperare la descrizione
     */
    @Input() value: { identity: { [key: string]: string | BigNumber | number; }, description?: string, isValid?: boolean; } | null;

    /**
     * Visualizza un loader per il caricamento
     */
    @Input() isLoading: boolean | null = null;

    /**
     * Se si abilita questa funzione al blur verrà ricercato un elemento per il testo inserito
     * Se non viene trovato verrà resituito errore
     */
    @Input() isExactTermFeatureEnabled: boolean = true;

    /**
     * Callback facoltativa solo se si abilita la funzionalità isExactTermFeatureEnabled
     */
    @Input() getExactItemFromTermCallback?: (term: string, showCodeInDescription: boolean, basePlainPascalCaseFixedIdentity: { [key: string]: string | BigNumber | number; } | null) => Observable<{ description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; }>;

    /**
     * Callback obbligatoria per recuperare l'item quando si ha solo l'identity senza description
     */
    @Input() getItemFromIdentityCallback: (identity: { [key: string]: string | BigNumber | number; }, showCodeInDescription: boolean, basePlainPascalCaseFixedIdentity: { [key: string]: string | BigNumber | number; } | null) => Observable<{ description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; }>;

    /**
     * Callback obbligatorio per recuperare la lista di item disponibili passando un termine di ricerca
     */
    @Input() getItemsFromTermCallback: (term: string, showCodeInDescription: boolean, basePlainPascalCaseFixedIdentity: { [key: string]: string | BigNumber | number; } | null) => Observable<{ description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; }[]>;
    @Input() getItemsCallback: (showCodeInDescription: boolean, basePlainPascalCaseFixedIdentity: { [key: string]: string | BigNumber | number; } | null) => Observable<{ description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; }[]>;

    @Input() enableOutsideClickListener = false;

    @Output() valueChange = new EventEmitter<{ identity: { [key: string]: string | BigNumber | number; }, description?: string, isValid?: boolean; }>();
    @Output() onBlur = new EventEmitter();
    @Output() onFocus = new EventEmitter();
    @Output() termValueChange = new EventEmitter();
    @Output() onFinishEditing = new EventEmitter();
    @Output() onKeyDown = new EventEmitter();
    @Output() onClear = new EventEmitter();
    @Output() onSearchFinished = new EventEmitter();
    readonly viewReady = output<void>();


    isViewReady = false;
    @ViewChild(NgSelectComponent, { static: false }) inputRef: NgSelectComponent;
    @ViewChild('popperError', { static: true }) popperError: NgxPopperjsDirective;
    @ViewChild('popperInfo', { static: true }) popperInfo: NgxPopperjsDirective;

    isLoading$ = new BehaviorSubject(false);
    isClearable$ = new BehaviorSubject(true);
    isOpen$ = new BehaviorSubject(false);
    isMobile$ = new BehaviorSubject(false);
    items$: Observable<{ description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; }[]>;
    itemInput$ = new Subject<string>();
    searchFinished: Subject<void> = new Subject();
    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    lastInputText: string;
    computedErrors: string[] = [];
    internalErrorList: string[] = [];
    externalBoxCommandUtility: ExternalBoxCommandUtility;
    isActive = false;
    uniqueId = `external-box-${nextId++}`;
    overrideBorderColor = null;
    isHover = false;
    computedPlaceholder = this._defaultPlaceholder;
    dropdownClosed = new Subject<void>();
    appendToCssSelector$ = new BehaviorSubject<string | null>(null);

    private isTouchScrolling = false;
    private touchStartY = 0;
    private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self'] as const, () => this.emitViewReady());

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly el: ElementRef,
        private readonly zone: NgZone,
        private readonly renderer: Renderer2,
        private readonly breakpointObserver: BreakpointObserver,
        private hostRef: ElementRef<HTMLElement>
    ) {
    }

    inputValue$: Observable<string>;

    getScrollableParentInfo(el: HTMLElement | null): {
        element: HTMLElement,
        className: string | null;
    } | null {
        while (el) {
            const style = getComputedStyle(el);

            const overflow = style.overflow;
            const overflowX = style.overflowX;
            const overflowY = style.overflowY;

            const scrollableX = this.isScrollableValue(overflowX) || (overflowX === 'visible' && this.isScrollableValue(overflow));
            const scrollableY = this.isScrollableValue(overflowY) || (overflowY === 'visible' && this.isScrollableValue(overflow));

            const isScrollable =
                (scrollableY && el.scrollHeight > el.clientHeight) ||
                (scrollableX && el.scrollWidth > el.clientWidth);

            if (isScrollable) {

                const elementFound = el;
                let classFound: string | null = null;

                // Controlla ogni classe dell'elemento e restituisce la prima univoca
                for (const cls of Array.from(el.classList)) {
                    const matches = document.getElementsByClassName(cls);
                    if (matches.length === 1) {
                        classFound = cls;
                    }
                }
                return { element: elementFound, className: classFound };
            }

            el = el.parentElement;
        }

        return null;
    }

    isScrollableValue(val: string): boolean {
        return val === 'auto' || val === 'scroll' || val === 'overlay';
    }



    // Helper per costruire un selettore CSS unico
    getCssSelector(el: Element): string {
        if (el.id) return `#${el.id}`;
        const path: string[] = [];
        while (el && el.nodeType === Node.ELEMENT_NODE) {
            let selector = el.nodeName.toLowerCase();
            if (el.className) {
                const classes = el.className.trim().split(/\s+/).join('.');
                selector += `.${classes}`;
            }

            const siblingIndex = Array.from(el.parentNode?.children || []).indexOf(el);
            selector += `:nth-child(${siblingIndex + 1})`;

            path.unshift(selector);
            el = el.parentElement!;
        }
        return path.join(' > ');
    }

    checkScrollableParent() {

        if (this.appendTo == null) {

            const el = this.hostRef.nativeElement;
            const scrollableParent = this.getScrollableParentInfo(el);

            if (scrollableParent) {
                // console.log(this.uniqueId + ': Parent scrollabile trovato:', scrollableParent);
                this.appendToCssSelector$.next('.' + scrollableParent.className);
            } else {
                // console.log(this.uniqueId + ': Nessun parent scrollabile trovato, uso il body');
                this.appendToCssSelector$.next('body');
            }
        } else {
            this.appendToCssSelector$.next(this.appendTo);
        }
    }

    ngAfterViewInit(): void {

        setTimeout(() => {
            this.checkScrollableParent();
        });

        this.inputValue$ = fromEvent(this.inputRef.searchInput.nativeElement, 'input').pipe(
            untilDestroyed(this),
            map((e: Event) => this.inputRef.searchInput.nativeElement.value),
            startWith(this.inputRef.searchInput.nativeElement.value)
        );

        this.inputRef.close = () => {
            if (!this.inputRef.isOpen || (this.inputRef as any)._manualOpen) {
                return;
            }
            this.inputRef.isOpen = false;
            (this.inputRef as any)._isComposing = false;

            this.inputRef.itemsList.unmarkItem();
            (this.inputRef as any)._onTouched();
            this.inputRef.closeEvent.emit();
            (this.inputRef as any)._cd.markForCheck();
        };

        const originalOpen = this.inputRef.open;

        // E' stato necessario fare un override della funzione open per ricalcolare il parent scrollabile e solo dopo richiamare la funzione originale in un timeout per dare tempo al DOM di aggiornarsi con il nuovo scrollable
        // altrimenti non si posiziona correttamente
        this.inputRef.open = () => {
            this.checkScrollableParent();
            setTimeout(() => originalOpen.call(this.inputRef));
        };

        queueMicrotask(() => {
            this.viewReadyCoordinator.markReady('self');
            this.viewReadyCoordinator.tryEmit(() => this.inputRef != null);
        });
    }

    showErroTooltip(): void {
        if (this.popperError) {
            PopperHelper.show(this.popperError);
        }
    }

    checkComputedErrors() {
        this.computedErrors = this.errorList.concat(this.internalErrorList);
        this.cd.detectChanges();
    }

    ngOnInit(): void {
        this.observeMobileBreakpoint();
        this.externalBoxCommandUtility = new ExternalBoxCommandUtility(this);
        this.loadItems();
        this.checkValue();
        this.typeToSearchText = MessageResourceManager.Current.getMessageWithStrings('AutoCompleteMinLengthMessage', this.minSearchLength.toString());

        if (this.enableOutsideClickListener) {
            this.bindDocumentClickListener();
        }
        this.handleOverridesColors();
    }

    private observeMobileBreakpoint(): void {
        this.breakpointObserver
            .observe([Breakpoints.Handset])
            .subscribe((result) => {
                this.isMobile$.next(result.matches);
                // this.cd.detectChanges(); // Aggiorna il rilevamento delle modifiche
            });
    }

    valuesAreEqual(val1: Object, val2: Object): boolean {
        if (val1 == null && val2 != null) {
            return false;
        }
        if (val2 == null && val1 != null) {
            return false;
        }
        if (val2 == null && val1 == null) {
            return true;
        }
        return JSON.stringify(val1) === JSON.stringify(val2);
    }

    show() {
        this.inputRef.open();
    }

    hide() {
        this.inputRef.close();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // this.loadValue();

        if (changes['value']) {

            this.debugInfo('ngOnChanges[value]');

            this.checkValue();
        }

        if (changes['errorList']) {
            if (changes['errorList'].currentValue != changes['errorList'].previousValue) {
                if (changes['errorList'].currentValue?.length === 0 && changes['errorList'].previousValue?.length === 0) {

                } else {
                    this.checkComputedErrors();
                    this.checkPopper();
                }

            }
        }

        if (changes['isLocked']) {
            this.checkLockedLogics();
        }

        if (changes['isDisabled']) {
            this.checkLockedLogics();
        }

        if (changes['minSearchLength']) {
            this.typeToSearchText = MessageResourceManager.Current.getMessageWithStrings('AutoCompleteMinLengthMessage', this.minSearchLength.toString());
        }

        if (changes['isLoading'] && this.isLoading !== null) {
            // Se isLoading esterno è true, prevale sullo stato interno
            this.isLoading$.next(this.isLoading);
            this.isClearable$.next(!this.isLoading);
        }

        if (changes['appendTo'] && this.appendTo !== null) {
            this.checkScrollableParent();
        }

    }

    onVisible(isVisible: boolean) {
        if (isVisible) {
            this.checkScrollableParent();
        }
    }

    checkLockedLogics() {
        if (this.isLocked && !this.isDisabled) {
            this.computedPlaceholder = this.isLockedMessage;
        } else {
            this.computedPlaceholder = this.placeholder ?? this._defaultPlaceholder;
        }
        this.cd.detectChanges();
    }

    async checkValue(): Promise<void> {
        this.debugInfo('checkValue');
        if (this.value?.identity == null && this.value?.isValid === true) {
            this.lastInputText = '';
            this.value = null;
            this.internalErrorList = [];
            this.inputRef.searchInput.nativeElement.value = '';
            (this.inputRef as any)._changeSearch(null);
            (this.inputRef as any).itemsList.resetFilteredItems();
        } else if (this.value == null || this.value?.identity == null) {
            this.lastInputText = '';
        } else {
            if (this.value?.isValid === true) {
                this.internalErrorList = [];
            }
            await this.loadDescriptionValue();
        }

        // Forza l'aggiornamento della lista degli item
        this.itemInput$.next('');

        this.updateSearchInput();
        this.checkComputedErrors();
        this.checkPopper();
        this.cd.detectChanges();
    }

    getDescriptionFromIdentity(identity: { [key: string]: string | BigNumber | number; }): string {
        this.debugInfo('getDescriptionFromIdentity');
        if (identity != null) {
            const values = Object.values(identity);
            if (values?.length > 0 && values[0] != null) {
                return values[0].toString();
            }
        }
        return '';
    }

    debugInfo(section: string) {
        if (this.showDebugInfo) {
            console.log(this.uniqueId, section, { value: this.value, lastInputText: this.lastInputText });
        }
    }

    async loadDescriptionValue(): Promise<void> {
        this.debugInfo('loadDescriptionValue');

        // se non è valido è inutile che provo a caricare la descrizione
        if (
            (
                this.value?.isValid == null
                || this.value?.isValid != false
            )
            &&
            (
                this.value?.description == null
                || this.value?.description?.length === 0
            )
        ) {
            // Aggiorniamo isLoading$ solo se l'isLoading esterno non è true
            if (this.isLoading !== true) {
                this.isLoading$.next(true);
                this.isClearable$.next(false);
            }

            if (this.value) {
                this.value.description = await firstValueFrom(
                    this.getItemFromIdentityCallback(
                        this.value.identity,
                        this.showCodeInDescription,
                        this.basePlainPascalCaseFixedIdentity
                    ).pipe(
                        map((item) => item?.description)
                    )
                );
            }

            // Aggiorniamo isLoading$ solo se l'isLoading esterno non è true
            if (this.isLoading !== true) {
                this.isLoading$.next(false);
                this.isClearable$.next(true);
            }
        }
    }

    mouseEnter(e) {
        this.isHover = true;
        this.handleOverridesColors();
    }

    mouseLeave(e) {
        this.isHover = false;
        this.handleOverridesColors();
    }

    private _calculateCurrentPosition(dropdownEl: HTMLElement) {

        if (this.inputRef.dropdownPanel.position !== 'auto') {
            return this.inputRef.dropdownPanel.position;
        }

        if (this.appendToCssSelector$.value) {
            const parentElement = document.querySelector(this.appendToCssSelector$.value);

            if (parentElement) {

                const parent = parentElement.getBoundingClientRect();

                const select = this.inputRef.element.getBoundingClientRect();

                const maxTop = parentElement.clientHeight + parent.top;

                if (select.top + select.height + dropdownEl.clientHeight > maxTop) {
                    return 'top';
                } else {
                    return 'bottom';
                }

            }

        }

        return 'bottom';

    }

    private _updateYPosition() {

        if (this.appendToCssSelector$.value) {
            const parentElement = document.querySelector(this.appendToCssSelector$.value);

            if (parentElement) {
                const select = this.inputRef.element.getBoundingClientRect();
                const parent = parentElement.getBoundingClientRect();

                const delta = select.height;

                if (this.inputRef.currentPanelPosition === 'top') {

                    const offsetBottom = parent.bottom - select.bottom - parentElement.scrollTop;

                    (this.inputRef.dropdownPanel as any)._dropdown.style.bottom = offsetBottom + delta + 'px';
                    (this.inputRef.dropdownPanel as any)._dropdown.style.top = 'auto';
                } else if (this.inputRef.currentPanelPosition === 'bottom') {

                    const offsetTop = select.top - parent.top + parentElement.scrollTop;

                    (this.inputRef.dropdownPanel as any)._dropdown.style.top = offsetTop + delta + 'px';
                    (this.inputRef.dropdownPanel as any)._dropdown.style.bottom = 'auto';
                }
            }
        }
    }

    private _updateXPosition() {

        if (this.appendToCssSelector$.value) {
            const parentElement = document.querySelector(this.appendToCssSelector$.value);

            if (parentElement instanceof HTMLElement) {
                const dropdown = (this.inputRef.dropdownPanel as any)._dropdown as HTMLElement;
                const select = this.inputRef.element.getBoundingClientRect();
                const parent = parentElement.getBoundingClientRect();
                const scrollLeft = parentElement.scrollLeft;
                const offsetLeft = select.left - parent.left + scrollLeft;
                const offsetRight = select.right - parent.left + scrollLeft;
                const visibleLeftBoundary = scrollLeft;
                const visibleRightBoundary = scrollLeft + parentElement.clientWidth;

                dropdown.style.width = select.width + 'px';
                dropdown.style.minWidth = select.width + 'px';

                const dropdownWidth = dropdown.clientWidth || select.width;
                let nextLeft = offsetLeft;

                if (nextLeft + dropdownWidth > visibleRightBoundary) {
                    nextLeft = offsetRight - dropdownWidth;
                }

                if (nextLeft < visibleLeftBoundary) {
                    nextLeft = visibleLeftBoundary;
                }

                if (nextLeft + dropdownWidth > visibleRightBoundary) {
                    nextLeft = Math.max(visibleLeftBoundary, visibleRightBoundary - dropdownWidth);
                }

                dropdown.style.left = nextLeft + 'px';
                dropdown.style.right = 'auto';
            }

        }

    }

    onDropdownOpen() {
        this.isOpen$.next(true);
        setTimeout(() => {
            this.initializeDropdownPosition();
            this.overrideDropdownCloseBehavior();
            this.bindEvents();
            this.handleOverridesColors();
        });
    }

    private initializeDropdownPosition(): void {
        if (this.inputRef.dropdownPanel) {
            (this.inputRef.dropdownPanel as any)._currentPosition = this._calculateCurrentPosition((this.inputRef.dropdownPanel as any)._dropdown);

            this.inputRef.dropdownPanel.adjustPosition = () => {
                this._updateYPosition();
                this._updateXPosition();
            };
            this.inputRef.dropdownPanel.adjustPosition();
        }
    }

    private overrideDropdownCloseBehavior(): void {
        if (this.inputRef.dropdownPanel) {
            (this.inputRef.dropdownPanel as any)._checkToClose = ($event: Event) => {
                if (this.shouldPreventDropdownClose($event)) {
                    return;
                }
                (this.inputRef.dropdownPanel as any)._zone.run(() => (this.inputRef.dropdownPanel as any).outsideClick.emit());
            };
        }
    }

    private shouldPreventDropdownClose($event: Event): boolean {
        if ($event instanceof TouchEvent && (this.isTouchScrolling || $event.type === 'touchmove' || $event.type === 'touchstart')) {
            return true;
        }

        const dropdownPanel = this.inputRef.dropdownPanel as any;
        if (dropdownPanel._select.contains($event.target) || dropdownPanel._dropdown.contains($event.target)) {
            return true;
        }

        const path = ($event as any).path || ($event as any).composedPath?.();
        if ($event.target && ($event.target as any).shadowRoot && path && path[0] && dropdownPanel._select.contains(path[0])) {
            return true;
        }

        return false;
    }

    private bindEvents(): void {
        const scrollableContainer = document.querySelector(this.appendToCssSelector$.value) as HTMLElement;

        if (scrollableContainer) {
            this.appendToCssSelector$.pipe(
                takeUntil(this.dropdownClosed),
                untilDestroyed(this),
                distinctUntilChanged(),
                switchMap(() =>
                    merge(
                        fromEvent<TouchEvent>(scrollableContainer, 'touchstart').pipe(tap((event: TouchEvent) => {
                            this.isTouchScrolling = false; // Reset dello stato
                            this.touchStartY = event.touches[0].clientY; // Salva la posizione iniziale
                        })),

                        fromEvent<TouchEvent>(scrollableContainer, 'touchmove').pipe(tap((event: TouchEvent) => {
                            const touchCurrentY = event.touches[0].clientY;

                            // Se c'è un movimento verticale, considera che è uno scroll touch
                            if (Math.abs(touchCurrentY - this.touchStartY) > 5) {
                                this.isTouchScrolling = true;
                            }
                        })),

                        fromEvent<TouchEvent>(scrollableContainer, 'touchend').pipe(tap((event: TouchEvent) => {
                            if (this.isTouchScrolling) {
                                // console.log('Scroll effettuato con il touch');
                            }
                        })),

                        fromEvent(scrollableContainer, 'scroll').pipe(throttleTime(1000), tap(() => {
                            if (!this.isMobile$.value) {
                                this.hide();
                                this.onFinishEditing.emit();
                                this.cd.detectChanges();
                            }
                        }))
                    )
                )
            ).subscribe();

        }
    }

    handleOverridesColors() {
        if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
            // devono essere impostate tutte e tre le variabili
            return;
        }

        this.overrideBorderColor = this.defaultBorderColor;
        if (this.isActive && !this.isDisabled) {
            this.overrideBorderColor = this.activeBorderColor;
        }
        if (this.isHover && !this.isDisabled) {
            this.overrideBorderColor = this.hoverBorderColor;
        }

        const container = this.inputRef.element.querySelector(".ng-select-container") as any;
        if (container) {
            container.style.borderColor = this.overrideBorderColor;
        }

        const dropdown = (this.inputRef.dropdownPanel as any)?._dropdown;
        if (dropdown) {
            dropdown.style.borderColor = this.overrideBorderColor;
        }

        const dropdownFooter = (this.inputRef.dropdownPanel as any)?._dropdown?.querySelector(".ng-dropdown-footer");
        if (dropdownFooter) {
            dropdownFooter.style.borderColor = this.overrideBorderColor;
        }
    }

    onDropdownClose() {
        this.isOpen$.next(false);
        this.dropdownClosed.next();
    }

    async blur(): Promise<void> {

        this.isActive = false;

        this.handleOverridesColors();

        this.hide();

        if (this.lastInputText?.length > 0 && this.isExactTermFeatureEnabled && this.lastInputText != this.getDescriptionFromCurrentValue()) {
            this.isLoading$.next(true);
            this.isClearable$.next(false);
            const result = await firstValueFrom(
                this.getExactItemFromTermCallback(
                    this.lastInputText,
                    this.showCodeInDescription,
                    this.basePlainPascalCaseFixedIdentity
                ).pipe(untilDestroyed(this)),
                { defaultValue: null }
            );
            this.isLoading$.next(false);
            this.isClearable$.next(true);
            if (result) {
                await this.onSelectedItem(result);
            } else if (result == null) {
                if (this.showDecodeError) {
                    this.internalErrorList = [MessageResourceManager.Current.getMessage('AutoCompleteTheCodeIsNotFoundMessage')];
                }
                await this.onSelectedItem({ identity: null, description: this.lastInputText, isValid: false });

            }
            // Forza l'aggiornamento della lista degli item
            this.itemInput$.next('');

        }

        this.onBlur.emit();
    }

    focus(event) {

        this.checkScrollableParent();

        this.isActive = true;

        this.handleOverridesColors();
        if (this.isDisabled) {
            return;
        }

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.updateSearchInput();

        this.onFocus.emit();

    }

    updateSearchInput() {

        if (this.isSearchable) {
            if (this.value?.identity != null) {

                this.inputRef.searchInput.nativeElement.value = this.getDescriptionFromCurrentValue();
                // this.inputRef.searchInput.nativeElement.value = this.initialValue ? this.initialValue : this.getCodeAndDescription();
                // this.initialValue = null;
                // this.inputRef.searchInput.nativeElement.dispatchEvent(new Event('input'));
                const pos = this.inputRef.searchInput.nativeElement.value ? this.inputRef.searchInput.nativeElement.value.length : 0;
                //     // this.inputRef.searchInput.nativeElement.setSelectionRange(pos, pos);
                this.inputRef.searchInput.nativeElement.selectionStart = 0;
                this.inputRef.searchInput.nativeElement.selectionEnd = pos;
                //     // if (!this.mouseDownOnSearchInput) {
                if (this.isActive) {
                    this.inputRef.searchInput.nativeElement.select();
                }

                // }

            } else if (this.value?.description != null && this.value?.description?.length) {
                this.inputRef.searchInput.nativeElement.value = this.value?.description;
                // this.inputRef.searchInput.nativeElement.dispatchEvent(new Event('input'));
                const pos = this.inputRef.searchInput.nativeElement.value ? this.inputRef.searchInput.nativeElement.value.length : 0;
                //     // this.inputRef.searchInput.nativeElement.setSelectionRange(pos, pos);
                this.inputRef.searchInput.nativeElement.selectionStart = 0;
                this.inputRef.searchInput.nativeElement.selectionEnd = pos;
                //     // if (!this.mouseDownOnSearchInput) {
                if (this.isActive) {
                    this.inputRef.searchInput.nativeElement.select();
                }
            } else if (this.value == null && this.inputRef?.searchInput?.nativeElement?.value?.length > 0) {
                if (this.inputRef) {
                    (this.inputRef as any)._clearSearch();
                    this.inputRef.searchInput.nativeElement.value = '';
                }
            } else if (this.value?.identity == null && this.value?.isValid == true) {
                if (this.inputRef) {
                    // (this.inputRef as any)._clearSearch();
                    (this.inputRef as any)._changeSearch(null);
                    (this.inputRef as any).itemsList.resetFilteredItems();

                }
            }
        }



        this.cd.detectChanges();
    }

    getDescriptionFromCurrentValue(): string {

        if (this.value?.description?.length > 0) {
            // Remove all kinds of line breaks - https://stackoverflow.com/a/10805198
            return this.value?.description.replace(/(\r\n|\n|\r)/gm, "");
        } else {
            if (this.value?.identity != null && this.singleKeyCode) {
                return this.singleKeyCode.toString();
            } else if (this.value?.identity != null && !this.singleKeyCode) {
                const values = Object.values(this.value.identity);
                if (values.length > 0 && values[0] != null) {
                    return values[0].toString();
                }
                return '';
            } else {
                return '';
            }
        }
    }

    clear() {
        this.lastInputText = '';
        if (this.inputRef) {
            // (this.inputRef as any)._clearSearch(
            this.inputRef.searchInput.nativeElement.value = '';
            (this.inputRef as any)._changeSearch(null);
            (this.inputRef as any).itemsList.resetFilteredItems();

        }
        this.checkComputedErrors();
        this.checkPopper();
        this.onClear.emit();
    }

    async onSelectedItem(e: { identity: { [key: string]: string | BigNumber | number; }, description: string, all?: object, isValid?: boolean; }): Promise<void> {
        this.debugInfo('onSelectedItem');
        this.hide();
        this.value = e;
        this.lastInputText = '';
        if (this.value != null) {
            await this.loadDescriptionValue();
        }
        this.valueChange.emit(this.value);

        this.updateSearchInput();

        // Resetto gli errori se ho resettato l'autocomplete
        if (e == null) {
            this.internalErrorList = [];
        }

        // Resetto gli errori se ho resettato l'autocomplete
        if (e?.isValid == true || e?.isValid == null) {
            this.internalErrorList = [];
        }
        this.checkComputedErrors();
        this.checkPopper();
    }

    keyDownFn(event: KeyboardEvent): boolean {
        this.debugInfo('keyDownFn');
        if (event.key === 'Tab') {

            // Se ho un elemento marcato nel dropdown
            if (this.inputRef?.dropdownPanel?.markedItem?.value) {
                const markedItem = this.inputRef.dropdownPanel.markedItem.value as { description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; };

                // Se l'identity è diversa da quella selezionata
                if (JSON.stringify(this.value?.identity) !== JSON.stringify(markedItem.identity)) {
                    void this.onSelectedItem(markedItem).then(() => this.blur());
                } else {
                    void this.blur();
                }
            } else {
                void this.blur();
            }

            // if (this)
            // setTimeout(() => this.onFocus(null))
        } else if (event.key === 'Enter') {
            if (!this.isOpen$.value) {
                this.show();
            } else {
                // Se ho un elemento marcato nel dropdown
                if (this.inputRef?.dropdownPanel?.markedItem?.value) {
                    const markedItem = this.inputRef.dropdownPanel.markedItem.value as { description: string, identity: { [key: string]: string | BigNumber | number; }, all: any; };

                    // Se l'identity è diversa da quella selezionata
                    if (JSON.stringify(this.value?.identity) !== JSON.stringify(markedItem.identity)) {
                        void this.onSelectedItem(markedItem).then(() => this.blur());
                    }

                }

                void this.blur();
            }

            // if (this)
            // setTimeout(() => this.onFocus(null))
        } else if (event.key === 'Del') {
            // TODO se si trova alla poszione zero con il cursore ed esiste un modello cancellare il modello
            // if (this.inputRef.)
        } else if (event.key === 'ArrowDown') {
            if (!this.isSearchable && !this.isOpen$.value) {
                return false;
            }
            if (this.isSearchable && !this.isOpen$.value && !this.openDropdownOnUpDownArrowKey) {
                return false;
            }
        } else if (event.key === 'ArrowUp') {
            if (!this.isSearchable && !this.isOpen$.value) {
                return false;
            }
            if (this.isSearchable && !this.isOpen$.value && !this.openDropdownOnUpDownArrowKey) {
                return false;
            }
        } else if (event.key === 'Backspace') {


            if (this.inputRef.searchInput?.nativeElement.value == '') {
                this.inputRef.clearModel();
            }
        }

        this.onKeyDown.emit(event);
        return true;
    }

    trackByFn(item: { description: string, identity: any, all: any; }) {
        if (item?.identity != null) {
            return JSON.stringify(item?.identity);
        }
        return null;
    }

    isSelectedItemDeactivated() {

    }

    zoom() {

    }

    isItemDeactivated(item) {

    }

    get singleKeyCode(): string | BigNumber | number | null {
        if (this.value?.identity) {
            const keys = Object.keys(this.value.identity);
            if (keys?.length === 1) {
                return this.value.identity[keys[0]];
            }
        }
        return null;
    }

    loadItems() {

        // Quando termina la ricerca
        this.searchFinished.pipe(
            untilDestroyed(this),
        ).subscribe(() => {

            if (this.isLoading !== true) {
                this.isLoading$.next(false);
                this.isClearable$.next(true);
            }
            this.onSearchFinished.emit();
        });

        this.itemInput$.pipe(
            untilDestroyed(this),
            // distinctUntilChanged()
        ).subscribe((term) => {
            if (term != null) {
                this.lastInputText = term;
                // Gestisce la casistica di cancellazione senza pulire l'elemento selezionato
                if (term !== '') {
                    this.termValueChange.emit(term);
                }
            }
        });

        if (this.isSearchable) {
            this.items$ = concat(
                of([]), // default items
                this.itemInput$.pipe(
                    untilDestroyed(this),
                    // distinctUntilChanged(),
                    debounceTime(300),
                    tap((term) => {
                        if (term?.length > 0) {

                            if (this.isLoading !== true) {
                                this.isLoading$.next(true);
                                this.isClearable$.next(false);
                            }
                        }
                    }),
                    switchMap((term: string) => this.termMapper(term))
                )
            );
        } else {

            this.items$ = this.getItemsCallback(this.showCodeInDescription, this.basePlainPascalCaseFixedIdentity).pipe(
                catchError(() => of([])), // empty list on error
                tap(() => this.searchFinished.next())
            );
        }
    }

    clearPoppers(): void {
        PopperHelper.hide(this.popperInfo);
        PopperHelper.hide(this.popperError);
    }

    toggleDropdown() {
        if (this.inputRef.isOpen) {
            this.hide();
        } else {
            this.show();
        }
    }

    private destroyListeners = new Subject<void>();

    private bindDocumentClickListener(): void {

        this.destroyListeners.next();
        this.zone.runOutsideAngular(() => {
            const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

            fromEvent(documentTarget, 'click').pipe(untilDestroyed(this), takeUntil(this.destroyListeners)).subscribe((event: MouseEvent) => {
                if (this.isOutsideClicked(event)) {
                    this.zone.run(() => {
                        this.onFinishEditing.emit();
                        this.cd.markForCheck();
                    });
                }
            });
        });
    }

    private isOutsideClicked(event: Event): boolean {
        return !(this.el.nativeElement.isSameNode(event.target) || this.isDropDownClicked(event) || this.isSearchInputClicked(event) || //this.dropdownOpening ||
            this.isDropdownItem(event) || this.el.nativeElement.contains(event.target) || this.isActionsClicked(event));
    }

    private isActionsClicked(event: Event): boolean {
        return this.el.nativeElement.querySelector('.autocomplete-actions')?.contains(event.target);
    }

    private isOutsideTouched(event: TouchEvent): boolean {
        return !(this.el.nativeElement.isSameNode(event.target) || this.isDropDownClicked(event) || this.isSearchInputClicked(event) || //this.dropdownOpening ||
            this.isDropdownItem(event) || this.el.nativeElement.contains(event.target));
    }

    private isDropDownClicked(event: Event): boolean {
        if (this.inputRef.dropdownPanel?.scrollElementRef?.nativeElement) {
            return (this.inputRef?.dropdownPanel as any)?._dropdown.contains(event.target);
        } else {
            return false;
        }
    }

    private isSearchInputClicked(event: Event): boolean {
        if (this.inputRef.searchInput?.nativeElement) {
            return (this.inputRef.searchInput?.nativeElement as any)?.contains(event.target);
        } else {
            return false;
        }
    }

    private isDropdownItem(event): boolean {
        return (event?.target?.className?.includes && event?.target?.className?.includes(this.inputRef?.dropdownId)) ||
            (event?.target?.parentNode?.className?.includes && event?.target?.parentNode?.className?.includes(this.inputRef?.dropdownId));
    }

    private termMapper(term: string): Observable<{
        description: string;
        identity: { [key: string]: string | BigNumber | number; };
        all: any;
    }[]> {
        // Se isLoading esterno è true, non modifichiamo lo stato
        if (
            term?.length > 0 &&
            this.singleKeyCode &&
            (term === this.singleKeyCode.toString() || term === this.getDescriptionFromCurrentValue()) &&
            this.errorList?.length === 0
        ) {
            // Aggiorniamo isLoading$ solo se l'isLoading esterno non è true
            if (this.isLoading !== true) {
                this.isLoading$.next(false);
                this.isClearable$.next(true);
            }
            return of([
                {
                    identity: this.value?.identity,
                    description: this.value?.description,
                    all: this.value
                }
            ]);

        } else if (term?.length >= this.minSearchLength && term === this.getDescriptionFromCurrentValue()) {
            // Aggiorniamo isLoading$ solo se l'isLoading esterno non è true
            if (this.isLoading !== true) {
                this.isLoading$.next(false);
                this.isClearable$.next(true);
            }
            return of([
                {
                    identity: this.value?.identity,
                    description: this.value?.description,
                    all: this.value
                }
            ]);
        } else if (term?.length >= this.minSearchLength) {

            this.show();

            return this.getItemsFromTermCallback(term, this.showCodeInDescription, this.basePlainPascalCaseFixedIdentity).pipe(
                map((items) => items.sort((x, y) => {
                    const lowerTerm = term.toLowerCase();

                    if (x.description.toLowerCase().startsWith(lowerTerm) && y.description.toLowerCase().startsWith(lowerTerm)) {
                        return x.description.localeCompare(y.description);
                    } else if (x.description.toLowerCase().startsWith(lowerTerm) && !y.description.toLowerCase().startsWith(lowerTerm)) {
                        return -1;
                    } else if (!x.description.toLowerCase().startsWith(lowerTerm) && y.description.toLowerCase().startsWith(lowerTerm)) {
                        return 1;
                    } else {
                        // (!x.description.toLowerCase().startsWith(lowerTerm) && !y.description.toLowerCase().startsWith(lowerTerm)) {
                        if (x.description.toLowerCase().indexOf(lowerTerm) > -1 && y.description.toLowerCase().indexOf(lowerTerm) == 0) {
                            return -1;
                        } else if (x.description.toLowerCase().indexOf(lowerTerm) == 0 && y.description.toLowerCase().indexOf(lowerTerm) > -1) {
                            return 1;
                        } else {
                            return x.description.localeCompare(y.description);
                        }
                    }
                })),
                catchError(() => of([])), // empty list on error
                tap(
                    () => {
                        // this.cd.detectChanges();
                        this.searchFinished.next();
                    }
                )
            );
        } else if (this.errorList?.length === 0 && this.value?.identity != null) {
            // Aggiorniamo isLoading$ solo se l'isLoading esterno non è true
            if (this.isLoading !== true) {
                this.isLoading$.next(false);
                this.isClearable$.next(true);
            }
            return of([
                {
                    identity: this.value?.identity,
                    description: this.value?.description,
                    all: this.value
                }
            ]);

        } else {
            this.searchFinished.next();
            return of([]);
        }
    }

    private checkPopper() {
        if (this.popperError && (this.computedErrors == null || this.computedErrors.length === 0)) {
            PopperHelper.hide(this.popperError);
        }
        if (this.popperInfo && this.computedErrors?.length > 0) {
            PopperHelper.hide(this.popperInfo);
        }
        if (this.popperInfo && (this.value?.description == null || this.value?.description?.length === 0)) {
            PopperHelper.hide(this.popperInfo);
        }
    }

    private emitViewReady(): void {
        this.isViewReady = true;
        this.viewReady.emit();
    }
}
